import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Marker } from "maplibre-gl";
import { useMsal } from "@azure/msal-react";
import { getRuntimeConfig } from "../../runtime-config";
import maplibregl from "maplibre-gl";

const config = getRuntimeConfig();
let protocolRegistered = false;

const structureLayers   = [{ id: "footprint", sourceLayer: "footprint" }];
const structureLayerIds = ["footprint"];


const MapStyleSwitcher = ({ map, markerPosition }) => {

  const { instance, accounts } = useMsal();

  const [activeStyle, setActiveStyle] = useState(
    () => sessionStorage.getItem("mapStyle") || "default"
  );

  const isApplyingStyleRef = useRef(false);
  const currentStyleRef    = useRef(null);
  const markerRef          = useRef(null);
  const tokenRef           = useRef(null);
  const tokenExpiryRef     = useRef(null);
  const initializedRef     = useRef(false);

  const azure_maps_id = config.AZ_MAPS_CLIENTID;

  //azure maps reg
  useEffect(() => {
    if (protocolRegistered) return;
    protocolRegistered = true;

    maplibregl.addProtocol("azuremaps", async (params, abortController) => {
      const url = params.url.replace("azuremaps://", "https://");
      const now = Date.now();

      if (!tokenRef.current || tokenExpiryRef.current < now) {
        try {
          const response = await instance.acquireTokenSilent({
            scopes: ["https://atlas.microsoft.com/.default"],
            account: accounts[0],
          });
          tokenRef.current       = response.accessToken;
          tokenExpiryRef.current = now + 50 * 60 * 1000;
        } catch (err) {
          console.error("Token acquisition failed:", err);
          throw err;
        }
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`,
          "x-ms-client-id": azure_maps_id,
        },
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Tile fetch failed: ${response.status}`);
      return { data: await response.arrayBuffer() };
    });
  }, []); 

  //Marker helper
  const addMarker = useCallback(
    (mapInst) => {
      if (!markerPosition?.lon || !markerPosition?.lat) return;

      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }

      markerRef.current = new Marker()
        .setLngLat([markerPosition.lon, markerPosition.lat])
        .addTo(mapInst);
    },
    [markerPosition]
  );

  // Re-add marker when position changes
  useEffect(() => {
    if (!markerPosition || !map) return;
    addMarker(map);
  }, [markerPosition, map, addMarker]);

  // ── Token helper ────────────────────────────────────────────────────────────
  const ensureToken = useCallback(async () => {
    const now = Date.now();
    if (!tokenRef.current || tokenExpiryRef.current < now) {
      const response = await instance.acquireTokenSilent({
        scopes: ["https://atlas.microsoft.com/.default"],
        account: accounts[0],
      });
      tokenRef.current       = response.accessToken;
      tokenExpiryRef.current = now + 50 * 60 * 1000;
    }
  }, [instance, accounts]);

  

 
  const updateMapStyle = useCallback(
    async (style, mapInst) => {
   
     
  
  if (currentStyleRef.current === style) { console.log("BAILED: same style"); return; }
  if (isApplyingStyleRef.current) { console.log("BAILED: already applying"); return; }
      if (currentStyleRef.current === style) return;
      if (isApplyingStyleRef.current) return;

      try {
        isApplyingStyleRef.current = true;

        await ensureToken();

        if (!mapInst.isStyleLoaded()) {
          mapInst.once("style.load", () => updateMapStyle(style, mapInst));
          return;
        }

        // Remove existing layers
        const layersToRemove = [
          "layer-background",
          "layer-land-cover",
          "layer-reserve",
          "layer-water",
          "layer-footprint",
          "layer-footprint-outline",
          "layer-transit",
          "layer-trail",
          "layer-road",
          "raster-tiles-layer",
          "layer-road-labels",
          "layer-road-casing",
          "layer-country-labels",
          "layer-medium-city-labels",
          "layer-small-city-labels",
          "layer-state-labels",
          "layer-neighborhood-labels",
          "layer-road-fill",
          ...structureLayerIds.flatMap((id) => [`layer-${id}`, `layer-${id}-outline`]),
        ];
        layersToRemove.forEach((id) => {
          try { if (mapInst.getLayer(id)) mapInst.removeLayer(id); }
          catch (e) { console.warn(`Could not remove layer ${id}:`, e); }
        });

        // Remove existing sources
        ["vector-tiles", "layer-road-casing", "raster-tiles", "building-tiles","label-tiles"].forEach((id) => {
          try { if (mapInst.getSource(id)) mapInst.removeSource(id); }
          catch (e) { console.warn(`Could not remove source ${id}:`, e); }
        });

        if (style === "default") {
          mapInst.addSource("vector-tiles", {
            type: "vector",
            tiles: [`azuremaps://atlas.microsoft.com/map/tile?api-version=2.0&tilesetId=microsoft.base&zoom={z}&x={x}&y={y}`],
            tileSize: 512,
            maxzoom: 20,
            attribution: "© Microsoft Azure Maps",
          });
            mapInst.addSource("label-tiles", {
            type: "vector",
            tiles: [`azuremaps://atlas.microsoft.com/map/tile?api-version=2.0&tilesetId=microsoft.base.labels&zoom={z}&x={x}&y={y}`],
            tileSize: 512,
            maxzoom: 20,
            attribution: "© Microsoft Azure Maps",
            });

            mapInst.addLayer({
            id: "layer-background",
            type: "fill",
            source: "vector-tiles",
            "source-layer": "vector_background",
            paint: { "fill-color": "#f0f2f0" },
          });

          mapInst.addLayer({
            id: "layer-land-cover",
            type: "fill",
            source: "vector-tiles",
            "source-layer": "land_cover_urban",
            paint: { "fill-color": "#f0f2f0" },
          });

          mapInst.addLayer({
            id: "layer-reserve",
            type: "fill",
            source: "vector-tiles",
            "source-layer": "reserve",
            paint: { "fill-color": "#e2e8de" },
          });

          mapInst.addLayer({
            id: "layer-water",
            type: "fill",
            source: "vector-tiles",
            "source-layer": "water_feature",
            paint: { "fill-color": "#dce4e8" },
          });

          mapInst.addLayer({
            id: "layer-road-casing",
            type: "line",
            source: "vector-tiles",
            "source-layer": "road",
            paint: {
              "line-color": "#d0d0d0",
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                6,  ["match", ["get", "p-entry"], "motorway", 3,  "highway", 2.5, "street", 1.5, "road", 1,  1],
                10, ["match", ["get", "p-entry"], "motorway", 8,  "highway", 6,   "street", 3,   "road", 2,  2],
                14, ["match", ["get", "p-entry"], "motorway", 18, "highway", 14,  "street", 6,   "road", 4,  4],
                18, ["match", ["get", "p-entry"], "motorway", 36, "highway", 28,  "street", 12,  "road", 8,  8],
              ],
            },
          });

          mapInst.addLayer({
            id: "layer-road-fill",
            type: "line",
            source: "vector-tiles",
            "source-layer": "road",
            paint: {
              "line-color": "#d0d0d0",
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                6,  ["match", ["get", "p-entry"], "motorway", 2,  "highway", 1.5, "street", 0.8, "road", 0.5, 0.5],
                10, ["match", ["get", "p-entry"], "motorway", 6,  "highway", 4,   "street", 2,   "road", 1.2, 1.2],
                14, ["match", ["get", "p-entry"], "motorway", 14, "highway", 10,  "street", 4,   "road", 2.5, 2.5],
                18, ["match", ["get", "p-entry"], "motorway", 28, "highway", 22,  "street", 8,   "road", 5,   5],
              ],
            },
          });

          mapInst.addLayer({
            id: "layer-road-labels",
            type: "symbol",
            source: "vector-tiles",
            "source-layer": "road",
            minzoom: 12,
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Open Sans Regular"],
              "text-size": ["interpolate", ["linear"], ["zoom"], 12, 8, 14, 11, 16, 13, 18, 15],
              "symbol-placement": "line",
              "text-max-angle": 30,
              "text-pitch-alignment": "viewport",
              "text-rotation-alignment": "map",
              "symbol-spacing": 300,
              "text-padding": 2,
            },
            paint: {
              "text-color": "#333333",
              "text-halo-color": "#ffffff",
              "text-halo-width": 1.5,
            },
          });

          structureLayers.forEach(({ id, sourceLayer }) => {
            mapInst.addLayer({
              id: `layer-${id}`,
              type: "fill",
              source: "vector-tiles",
              "source-layer": sourceLayer,
              paint: { "fill-color": "#dcdee0", "fill-opacity": 0.9 },
            });
            mapInst.addLayer({
              id: `layer-${id}-outline`,
              type: "line",
              source: "vector-tiles",
              "source-layer": sourceLayer,
              paint: { "line-color": "#b0b2b4", "line-width": 0.5 },
            });
          });

          // const addStructureLayers = (mapInst) => {
          //     structureLayers.forEach(({ id, sourceLayer }) => {
          //       if (mapInst.getLayer(`layer-${id}`)) return; // guard duplicates

          //       mapInst.addLayer({
          //         id: `layer-${id}`,
          //         type: "fill",
          //         source: "vector-tiles",
          //         "source-layer": sourceLayer,
          //         paint: { "fill-color": "#dcdee0", "fill-opacity": 0.9 },
          //       });
          //       mapInst.addLayer({
          //         id: `layer-${id}-outline`,
          //         type: "line",
          //         source: "vector-tiles",
          //         "source-layer": sourceLayer,
          //         paint: { "line-color": "#b0b2b4", "line-width": 0.5 },
          //       });
          //     });
          //   };


          // if (mapInst.isSourceLoaded("vector-tiles")) {
          //   console.log("Vector loaded")
          //     addStructureLayers(mapInst);
          //   } else {
          //     mapInst.on("sourcedata", (e) => {
          //       if (e.sourceId === "vector-tiles" && e.isSourceLoaded) {
          //         addStructureLayers(mapInst);
          //       }
          //     });
          //   }




          mapInst.addLayer({
            id: "layer-transit",
            type: "line",
            source: "vector-tiles",
            "source-layer": "transit",
            paint: { "line-color": "#a855f7", "line-width": 1.5 },
          });

            //labels
           
            mapInst.addLayer({
              id: "layer-country-labels",
              type: "symbol",
              source: "label-tiles",
              "source-layer": "Country name",
              maxzoom: 8,
              layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Regular"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  2, 10,
                  6, 16,
                ],
                "symbol-placement": "point",
                "text-max-width": 8,
              },
              paint: {
                "text-color": "#222222",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
              },
            });

           
            mapInst.addLayer({
              id: "layer-medium-city-labels",
              type: "symbol",
              source: "label-tiles",
              "source-layer": "Medium city",
              minzoom: 6,
              maxzoom: 14,
              layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Regular"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  6,  10,
                  10, 14,
                  14, 18,
                ],
                "symbol-placement": "point",
                "text-max-width": 8,
              },
              paint: {
                "text-color": "#222222",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.5,
              },
            });

            
            mapInst.addLayer({
              id: "layer-small-city-labels",
              type: "symbol",
              source: "label-tiles",
              "source-layer": "Small city",
              minzoom: 8,
              maxzoom: 14,
              layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Regular"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  8,  9,
                  12, 13,
                ],
                "symbol-placement": "point",
                "text-max-width": 11,
              },
              paint: {
                "text-color": "#333333",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.5,
              },
            });

            // State border labels
            mapInst.addLayer({
              id: "layer-state-labels",
              type: "symbol",
              source: "label-tiles",
              "source-layer": "State border",
              minzoom: 5,
              maxzoom: 10,
              layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Regular"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  5, 9,
                  10, 13,
                ],
                "symbol-placement": "point",
                "text-transform": "uppercase",
                "text-letter-spacing": 0.1,
                "text-max-width": 8,
              },
              paint: {
                "text-color": "#666666",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1,
              },
            });

            // Neighborhood labels from existing vector-tiles source
            mapInst.addLayer({
              id: "layer-neighborhood-labels",
              type: "symbol",
              source: "vector-tiles",
              "source-layer": "neighborhood",
              minzoom: 12,
              layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Regular"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  12, 10,
                  16, 13,
                ],
                "symbol-placement": "point",
                "text-max-width": 8,
              },
              paint: {
                "text-color": "#666666",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1,
              },
            });


        } else if (style === "satellite") {
          mapInst.addSource("raster-tiles", {
            type: "raster",
            tiles: [`azuremaps://atlas.microsoft.com/map/tile?api-version=2.0&tilesetId=microsoft.imagery&zoom={z}&x={x}&y={y}`],
            tileSize: 256,
            maxzoom: 20,
            attribution: "© Microsoft Azure Maps",
          });

          mapInst.addLayer({
            id: "raster-tiles-layer",
            type: "raster",
            source: "raster-tiles",
          });
        }

        currentStyleRef.current = style;
        addMarker(mapInst);

      } catch (error) {
        console.error("Error updating map style:", error);
      } finally {
        isApplyingStyleRef.current = false;
      }
    },
    [addMarker, ensureToken]
  );

 useEffect(() => {
  if (!map || initializedRef.current) return;
  initializedRef.current = true;

  const storedStyle = sessionStorage.getItem("mapStyle") || "default";

  const applyStyle = () => {
    updateMapStyle(storedStyle, map).then(() => setActiveStyle(storedStyle));
  };

  
  const ensureGlyphsThenApply = () => {
    const currentStyle = map.getStyle();

    if (!currentStyle.glyphs) {
      
      map.setGlyphs("https://fonts.openmaptiles.org/{fontstack}/{range}.pbf");
    }

    applyStyle();
  };

  if (!map.isStyleLoaded()) {
    map.once("style.load", ensureGlyphsThenApply);
  } else {
    ensureGlyphsThenApply();
  }
}, [map, updateMapStyle]);
  
  const handleStyleChange = useCallback(
    (newStyle) => {
      if (newStyle === activeStyle) return;

      setActiveStyle(newStyle);
      sessionStorage.setItem("mapStyle", newStyle);

      if (!map) return;

      if (!map.isStyleLoaded()) {
        map.once("style.load", () => updateMapStyle(newStyle, map));
        return;
      }

      updateMapStyle(newStyle, map);
    },
    [activeStyle, map, updateMapStyle]
  );

 
  const buttonBase = {
    position: "absolute",
    zIndex: 1,
    borderRadius: "10px",
    padding: "0",
    overflow: "hidden",
    width: "40px",
    height: "40px",
    bottom: "100px",
  };

  return (
    <div className="absolute right-4 top-4 flex flex-col bg-white rounded-lg shadow-md overflow-hidden">
      <button
        className={`flex items-center justify-center ${
          activeStyle === "default"
            ? "bg-blue-100 border-2 border-blue-500"
            : "bg-white hover:bg-gray-100"
        }`}
        onClick={() => handleStyleChange("default")}
        aria-label="Default map style"
        style={{ ...buttonBase, left: "24px" }}
      >
        <div className="w-full h-full overflow-hidden">
          <img src="\assets\Default.png" alt="Default map style" className="w-full h-full object-cover" />
        </div>
      </button>

      <button
        className={`flex items-center justify-center ${
          activeStyle === "satellite"
            ? "bg-black-10 border-1 border-blue-10"
            : "bg-black hover:bg-gray-10"
        }`}
        onClick={() => handleStyleChange("satellite")}
        aria-label="Satellite map style"
        style={{ ...buttonBase, left: "75px", border: "none", boxShadow: "none" }}
      >
        <div className="w-full h-full overflow-hidden">
          <img src="\assets\Statelite.png" alt="Satellite map style" className="w-full h-full object-cover" />
        </div>
      </button>
    </div>
  );
};

export default memo(MapStyleSwitcher, (prevProps, nextProps) => {
  return (
    prevProps.map            === nextProps.map &&
    prevProps.markerPosition === nextProps.markerPosition
  );
});